'use client'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useSyncExternalStore,
    type ReactElement,
    type ReactNode,
} from 'react'

const COOKIE_CONSENT_STORAGE_KEY = 'baysic_cookie_consent'
const COOKIE_CONSENT_VERSION = '1'
const COOKIE_CONSENT_EVENT = 'baysic-cookie-consent-change'

interface CookieConsentContextValue {
    isReady: boolean
    analyticsAllowed: boolean
    acceptAnalytics: () => void
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(
    null
)

const readStoredConsent = (): boolean => {
    if (typeof window === 'undefined') {
        return false
    }

    try {
        const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)
        if (!raw) {
            return false
        }

        const parsed = JSON.parse(raw) as {
            accepted?: boolean
            version?: string
        }

        return (
            parsed.accepted === true &&
            parsed.version === COOKIE_CONSENT_VERSION
        )
    } catch {
        return false
    }
}

const subscribe = (onStoreChange: () => void): (() => void) => {
    const handler = (): void => onStoreChange()
    window.addEventListener(COOKIE_CONSENT_EVENT, handler)
    window.addEventListener('storage', handler)
    return () => {
        window.removeEventListener(COOKIE_CONSENT_EVENT, handler)
        window.removeEventListener('storage', handler)
    }
}

export function CookieConsentProvider({
    children,
}: {
    children: ReactNode
}): ReactElement {
    const isReady = useSyncExternalStore(
        (): (() => void) => (): void => {},
        (): boolean => true,
        (): boolean => false
    )
    const analyticsAllowed = useSyncExternalStore(
        subscribe,
        readStoredConsent,
        () => false
    )

    const acceptAnalytics = useCallback((): void => {
        localStorage.setItem(
            COOKIE_CONSENT_STORAGE_KEY,
            JSON.stringify({
                accepted: true,
                version: COOKIE_CONSENT_VERSION,
                acceptedAt: new Date().toISOString(),
            })
        )
        window.dispatchEvent(new Event(COOKIE_CONSENT_EVENT))
    }, [])

    const value = useMemo(
        () => ({
            isReady,
            analyticsAllowed,
            acceptAnalytics,
        }),
        [isReady, analyticsAllowed, acceptAnalytics]
    )

    return (
        <CookieConsentContext.Provider value={value}>
            {children}
        </CookieConsentContext.Provider>
    )
}

export function useCookieConsent(): CookieConsentContextValue {
    const context = useContext(CookieConsentContext)

    if (!context) {
        throw new Error(
            'useCookieConsent must be used within CookieConsentProvider'
        )
    }

    return context
}
